/**
 * SmartLink publisher whitelist / blacklist — data-layer client.
 *
 * Thin typed wrapper over `apiClient` for the
 * `/api/v1/smartlink-pub-list` surface (feature #114). Mirrors the
 * contract declared in `apps/api/src/routes/smartlink-pub-list.ts`.
 *
 * Keep this file dependency-free beyond the root `apiClient` — no React,
 * no TanStack imports — so pure helpers can be unit-tested in a vanilla
 * node environment (no DOM required) from the colocated `.test.tsx`.
 *
 * Surface (per API route):
 *   POST   /api/v1/smartlink-pub-list        body: { smartlink_id, publisher_id, list_kind }
 *   DELETE /api/v1/smartlink-pub-list/:id
 *   GET    /api/v1/smartlink-pub-list/by-smartlink/:id
 *   POST   /api/v1/smartlink-pub-list/check  body: { smartlink_id, publisher_id }
 *
 * The dash uses two discrete UI surfaces on top of this contract:
 *   1. a "mode toggle" that flips the DEFAULT list_kind used by the add
 *      form (allow mode → list_kind=allow, deny mode → list_kind=deny).
 *   2. an "access tester" that calls POST /check and surfaces the
 *      returned reason + allow/deny badge.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Domain types — mirror the API's PubListKind / PubListView / CheckResult.
// The backend strips tenant_id before returning rows, so the dash-side row
// shape matches its `rowToView` output exactly.
// ---------------------------------------------------------------------------

export const PUB_LIST_KINDS = ["allow", "deny"] as const;
export type PubListKind = (typeof PUB_LIST_KINDS)[number];

export type PubListMode = "allow" | "deny";

export interface PubListEntry {
	id: string;
	smartlink_id: string;
	publisher_id: string;
	list_kind: PubListKind;
	created_at: number;
}

export interface PubListByResponse {
	smartlink_id: string;
	mode: "allow" | "deny" | "open";
	allow: PubListEntry[];
	deny: PubListEntry[];
	entries: PubListEntry[];
}

export interface CheckResponse {
	smartlink_id: string;
	publisher_id: string;
	allowed: boolean;
	mode: "allow" | "deny" | "open";
	reason?: string;
}

// ---------------------------------------------------------------------------
// Pure helpers — colocated with the data layer so they can be imported by
// both the page component and the test harness without pulling React in.
// ---------------------------------------------------------------------------

/**
 * Infer the default list_kind to submit with the add form from the current
 * page mode. Mode "allow" ⇒ add entries as `allow` (whitelist), mode "deny"
 * ⇒ add entries as `deny` (blacklist). Flipping the toggle flips the
 * persisted default — this is what the mode-toggle test exercises.
 */
export function inferListKindFromMode(mode: PubListMode): PubListKind {
	return mode === "allow" ? "allow" : "deny";
}

/**
 * Flip the mode. Kept as a named helper (vs inline ternary) so the
 * ModeToggle component and the test share a single source of truth.
 */
export function toggleMode(mode: PubListMode): PubListMode {
	return mode === "allow" ? "deny" : "allow";
}

/**
 * Remove a single entry by id from a local entries array. Pure — used by
 * the delete-row path so the UI can optimistically update before the
 * network round-trip completes, and by the test to assert the row-delete
 * contract.
 */
export function removeRowById<T extends { id: string }>(
	rows: readonly T[],
	id: string,
): T[] {
	return rows.filter((r) => r.id !== id);
}

/**
 * Split a flat entries list into allow/deny buckets preserving insertion
 * order — used by the page to render the "Current entries" table grouped
 * by kind.
 */
export function partitionByKind(entries: readonly PubListEntry[]): {
	allow: PubListEntry[];
	deny: PubListEntry[];
} {
	const allow: PubListEntry[] = [];
	const deny: PubListEntry[] = [];
	for (const e of entries) {
		if (e.list_kind === "allow") allow.push(e);
		else if (e.list_kind === "deny") deny.push(e);
	}
	return { allow, deny };
}

/**
 * Normalise a user-entered id-ish string before hitting the API. Mirrors
 * the API's `normaliseIdField` contract: trim, reject empty, cap at 256.
 */
export function normaliseId(raw: string): string | null {
	const t = raw.trim();
	if (t.length === 0) return null;
	if (t.length > 256) return null;
	return t;
}

// ---------------------------------------------------------------------------
// Validation — hand-rolled `.safeParse` surface, matching the convention
// used by other dash features so we don't pull zod into the feature.
// ---------------------------------------------------------------------------

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export interface AddEntryFormInput {
	smartlink_id: string;
	publisher_id: string;
	list_kind: PubListKind;
}

export const addEntrySchema = {
	safeParse(input: AddEntryFormInput): ParseResult<AddEntryFormInput> {
		const errors: Record<string, string> = {};

		const smartlinkId = normaliseId(input.smartlink_id ?? "");
		if (smartlinkId === null) {
			errors.smartlink_id = "SmartLink ID is required (≤256 chars).";
		}
		const publisherId = normaliseId(input.publisher_id ?? "");
		if (publisherId === null) {
			errors.publisher_id = "Publisher ID is required (≤256 chars).";
		}
		if (input.list_kind !== "allow" && input.list_kind !== "deny") {
			errors.list_kind = "List kind must be allow or deny.";
		}

		if (Object.keys(errors).length > 0) {
			return { success: false, errors };
		}
		return {
			success: true,
			data: {
				smartlink_id: smartlinkId!,
				publisher_id: publisherId!,
				list_kind: input.list_kind,
			},
		};
	},
};

export interface CheckFormInput {
	smartlink_id: string;
	publisher_id: string;
}

export const checkSchema = {
	safeParse(input: CheckFormInput): ParseResult<CheckFormInput> {
		const errors: Record<string, string> = {};
		const smartlinkId = normaliseId(input.smartlink_id ?? "");
		if (smartlinkId === null) errors.smartlink_id = "SmartLink ID is required.";
		const publisherId = normaliseId(input.publisher_id ?? "");
		if (publisherId === null) errors.publisher_id = "Publisher ID is required.";
		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: { smartlink_id: smartlinkId!, publisher_id: publisherId! },
		};
	},
};

// ---------------------------------------------------------------------------
// Remote operations — TanStack Query consumers import these directly.
// ---------------------------------------------------------------------------

const BASE = "/smartlink-pub-list";

/**
 * Fetch all entries (allow + deny) for a smartlink. 404 is normalised to an
 * empty open-mode response so the page can render an empty table without
 * throwing.
 */
export async function fetchBySmartlink(
	smartlinkId: string,
): Promise<PubListByResponse> {
	try {
		return await apiClient.get<PubListByResponse>(
			`${BASE}/by-smartlink/${encodeURIComponent(smartlinkId)}`,
		);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) {
			return {
				smartlink_id: smartlinkId,
				mode: "open",
				allow: [],
				deny: [],
				entries: [],
			};
		}
		throw err;
	}
}

export interface AddEntryArgs {
	smartlink_id: string;
	publisher_id: string;
	list_kind: PubListKind;
}

/**
 * POST a single allow/deny entry. The API emits 409 when the
 * (smartlink_id, publisher_id) pair already exists; callers should surface
 * that error to the operator so they can delete-then-re-add to change
 * list_kind.
 */
export async function addEntry(args: AddEntryArgs): Promise<PubListEntry> {
	return await apiClient.post<PubListEntry>(`${BASE}`, args);
}

/** DELETE /:id — drop a single entry. Silent on 404 (already gone). */
export async function deleteEntry(id: string): Promise<void> {
	try {
		await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

export interface CheckArgs {
	smartlink_id: string;
	publisher_id: string;
}

/** POST /check — evaluate publisher access against current rules. */
export async function checkAccess(args: CheckArgs): Promise<CheckResponse> {
	return await apiClient.post<CheckResponse>(`${BASE}/check`, args);
}

// ---------------------------------------------------------------------------
// Query keys — stable cache identity for TanStack Query.
// ---------------------------------------------------------------------------

export const pubListQueryKeys = {
	all: ["smartlinks-pub-list"] as const,
	bySmartlink: (smartlinkId: string) =>
		[...pubListQueryKeys.all, "by-smartlink", smartlinkId] as const,
};
